import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Textarea } from '../../ui/textarea';
import { oliApi, erroreOli } from './api';

const CAMPI = ['data_prelievo', 'quantita_kg', 'numero_fir', 'trasportatore_id', 'destinatario_id',
  'conducente_nome', 'conducente_cognome', 'targa_automezzo', 'note'];
const CAMPO = 'w-full h-11 px-3 rounded-lg border border-gray-300 mt-1';

const Campo = ({ label, children }) => <label className="text-sm font-medium text-gray-700 block">{label}{children}</label>;

// Correzione con traccia: si salvano solo i campi cambiati, con il motivo (solo responsabile)
export const CorreggiRitiroDialog = ({ prelievo, trasportatori, destinatari, onClose, onSaved }) => {
  const [form, setForm] = useState({});
  const [motivo, setMotivo] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!prelievo) return;
    setForm(Object.fromEntries(CAMPI.map((k) => [k, prelievo[k] ?? ''])));
    setMotivo('');
  }, [prelievo]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const cambiati = prelievo ? CAMPI.filter((k) => String(form[k] ?? '') !== String(prelievo[k] ?? '')) : [];

  const salva = async () => {
    const body = { motivo: motivo.trim() };
    cambiati.forEach((k) => { body[k] = k === 'quantita_kg' ? Number(form[k]) : form[k]; });
    setBusy(true);
    try {
      await oliApi.put(`/prelievi/${prelievo.id}`, body);
      toast.success('Correzione registrata');
      onSaved();
    } catch (e) {
      toast.error(erroreOli(e, 'Correzione non riuscita'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!prelievo} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg rounded-2xl max-h-[88vh] overflow-y-auto" data-testid="oli-correggi-dialog">
        <DialogHeader>
          <DialogTitle>Correggi ritiro n. {prelievo?.numero_registro}</DialogTitle>
          <DialogDescription>Il valore di prima resta nel registro con il motivo, chi ha corretto e quando.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Campo label="Data ritiro"><input type="date" value={form.data_prelievo || ''} onChange={set('data_prelievo')} className={CAMPO} data-testid="oli-correggi-data" /></Campo>
          <Campo label="Kg"><input type="number" step="0.1" value={form.quantita_kg} onChange={set('quantita_kg')} className={CAMPO} data-testid="oli-correggi-kg" /></Campo>
        </div>
        <Campo label="Numero FIR"><input value={form.numero_fir || ''} onChange={set('numero_fir')} className={CAMPO} data-testid="oli-correggi-fir" /></Campo>
        <Campo label="Trasportatore">
          <select value={form.trasportatore_id || ''} onChange={set('trasportatore_id')} className={CAMPO} data-testid="oli-correggi-trasportatore">
            {trasportatori.map((t) => <option key={t.id} value={t.id}>{t.denominazione}</option>)}
          </select>
        </Campo>
        <Campo label="Impianto">
          <select value={form.destinatario_id || ''} onChange={set('destinatario_id')} className={CAMPO} data-testid="oli-correggi-impianto">
            {destinatari.map((d) => <option key={d.id} value={d.id}>{d.denominazione}</option>)}
          </select>
        </Campo>
        <div className="grid grid-cols-2 gap-3">
          <Campo label="Conducente"><input value={form.conducente_nome || ''} onChange={set('conducente_nome')} className={CAMPO} data-testid="oli-correggi-conducente" /></Campo>
          <Campo label="Targa"><input value={form.targa_automezzo || ''} onChange={(e) => setForm({ ...form, targa_automezzo: e.target.value.toUpperCase() })} className={CAMPO} data-testid="oli-correggi-targa" /></Campo>
        </div>
        <Campo label="Note"><input value={form.note || ''} onChange={set('note')} className={CAMPO} data-testid="oli-correggi-note" /></Campo>
        <Campo label="Motivo della correzione *">
          <Textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={2} className="mt-1"
            placeholder="es. kg letti male dal FIR" data-testid="oli-correggi-motivo" />
        </Campo>
        <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={onClose} data-testid="oli-correggi-chiudi">Chiudi</Button>
          <Button onClick={salva} disabled={busy || !cambiati.length || motivo.trim().length < 3}
            className="bg-orange-700 hover:bg-orange-800 text-white" data-testid="oli-correggi-salva">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Salva correzione${cambiati.length ? ` (${cambiati.length})` : ''}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
